import { PlusIcon, TrashIcon } from '@phosphor-icons/react'
import { useEffect } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { v4 as uuid } from 'uuid'

import { SwitchFormField } from '@/components/form/switch-form-field'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { Button } from '@/components/ui/button'
import { Divider } from '@/components/ui/divider'
import { ErrorMessage } from '@/components/ui/error-message'
import { Icon } from '@/components/ui/icon'
import { Tooltip } from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'

import type { PackagesSettingsForm } from '../packages-settings.types'
import { MAX_LOCATION_NAME_LENGTH } from '../packages-settings.types'
import {
  getLocationsTip,
  getPackageRoomTip,
  getRequireLocationTip,
} from './locations-tab.constants'
import * as S from './locations-tab.styled'

type LocationsTabProps = {
  packageRoomAvailable: boolean
}

export const LocationsTab = ({ packageRoomAvailable }: LocationsTabProps) => {
  const { t } = useTranslation(['packages-settings'])
  const {
    setValue,
    formState: { errors },
  } = useFormContext<PackagesSettingsForm>()

  const customLocationsEnabled = useWatch<PackagesSettingsForm, 'customLocationsEnabled'>({
    name: 'customLocationsEnabled',
  })
  const locationRequired = useWatch<PackagesSettingsForm, 'locationRequired'>({
    name: 'locationRequired',
  })

  const { fields, append, remove } = useFieldArray<PackagesSettingsForm, 'locations'>({
    name: 'locations',
  })

  useEffect(() => {
    if (!customLocationsEnabled) {
      setValue('locationRequired', false, { shouldDirty: true })
    }
  }, [customLocationsEnabled, setValue])

  const locationsError =
    (errors.locations as { message?: string } | undefined)?.message ??
    (errors.locations as { root?: { message?: string } } | undefined)?.root?.message ??
    errors.locationRequired?.message ??
    ''

  return (
    <S.Container>
      <SwitchFormField
        name="customLocationsEnabled"
        label={t('packages-settings:locationsTab.customLocationsEnabled.label')}
        tooltip={getLocationsTip(t)}
        disabled={packageRoomAvailable}
        inlineLabel={
          customLocationsEnabled
            ? t('packages-settings:common.yes')
            : t('packages-settings:common.no')
        }
      />

      <Divider color="gray500" />

      <SwitchFormField
        name="locationRequired"
        label={t('packages-settings:locationsTab.locationRequired.label')}
        tooltip={getRequireLocationTip(t)}
        disabled={!customLocationsEnabled}
        inlineLabel={
          locationRequired ? t('packages-settings:common.yes') : t('packages-settings:common.no')
        }
      />

      {customLocationsEnabled && (
        <S.LocationsSection>
          <S.LocationsList>
            {fields.map((field, idx) => (
              <S.LocationRow key={field.id}>
                <S.NumberCell>
                  <Typography size="text-medium" color="primary500">
                    {idx + 1}
                  </Typography>
                </S.NumberCell>
                {field.packageRoom ? (
                  <S.FixedLocationContent>
                    <Typography size="text-medium" color="primary800">
                      {field.name}
                    </Typography>
                    <S.TooltipWrapper>
                      <Tooltip>{getPackageRoomTip(t)}</Tooltip>
                    </S.TooltipWrapper>
                  </S.FixedLocationContent>
                ) : (
                  <>
                    <S.InputCell>
                      <TextInputFormField
                        name={`locations.${idx}.name`}
                        size="small"
                        placeholder={t('packages-settings:locationsTab.locationNamePlaceholder')}
                        maxLength={MAX_LOCATION_NAME_LENGTH}
                      />
                    </S.InputCell>
                    <S.DeleteCell>
                      <Button onlyBox use="secondary" size="small" onClick={() => remove(idx)}>
                        <Icon color="red500" icon={TrashIcon} size="1.6rem" />
                      </Button>
                    </S.DeleteCell>
                  </>
                )}
              </S.LocationRow>
            ))}
          </S.LocationsList>

          {fields.length < 10 && (
            <S.AddOptionWrapper>
              <Button
                use="secondary"
                size="medium"
                onClick={() => append({ id: uuid(), name: '' })}
              >
                {t('packages-settings:locationsTab.addOption')}
                <Icon icon={PlusIcon} />
              </Button>
            </S.AddOptionWrapper>
          )}
        </S.LocationsSection>
      )}

      <ErrorMessage>{locationsError}</ErrorMessage>
    </S.Container>
  )
}
